import {
  type ChatClient,
  type ChatRoomSummary,
  MAX_TOPIC_LENGTH,
  normaliseTopic,
} from '@wtfalch/chat';
import { Button, Input } from '@wtfalch/design';
import { useEffect, useRef, useState } from 'react';

/** The room's topic under its name. Plain text only. Someone who may set it
 *  (`canSetTopic`) presses it to edit in place; a failed save says so and the
 *  topic stays as it was. Draws nothing when there is no topic and the
 *  person cannot add one. */
export function RoomTopic({ client, room }: { client: ChatClient; room: ChatRoomSummary }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const running = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: another room is the trigger; its edit state does not carry over.
  useEffect(() => {
    setEditing(false);
    setError(null);
  }, [room.id]);

  const topic = room.topic ?? '';
  if (!room.canSetTopic && topic === '') return null;

  if (!editing || !room.canSetTopic) {
    if (!room.canSetTopic) return <span className="text-xs text-muted truncate">{topic}</span>;
    return (
      <button
        type="button"
        className="text-xs text-muted truncate text-left chat-topic"
        aria-label={topic === '' ? 'Add a topic' : `Edit topic: ${topic}`}
        onClick={() => {
          setDraft(topic);
          setError(null);
          setEditing(true);
        }}
      >
        {topic === '' ? 'Add a topic' : topic}
      </button>
    );
  }

  const cancel = () => {
    setEditing(false);
    setError(null);
  };

  const save = async () => {
    if (running.current) return;
    const next = normaliseTopic(draft);
    if (next === topic) {
      cancel();
      return;
    }
    running.current = true;
    setBusy(true);
    setError(null);
    try {
      await client.setRoomTopic(room.id, next);
      setEditing(false);
    } catch (e) {
      console.error('Could not set the topic:', e);
      setError("Couldn't save the topic. It is unchanged.");
    } finally {
      running.current = false;
      setBusy(false);
    }
  };

  return (
    <span className="flex items-center gap-2 min-w-0 chat-topic-edit">
      <Input
        size="sm"
        aria-label="Topic"
        className="grow min-w-0"
        value={draft}
        maxLength={MAX_TOPIC_LENGTH}
        disabled={busy}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void save();
          else if (e.key === 'Escape') cancel();
        }}
      />
      <Button size="sm" busy={busy} disabled={busy} onPress={() => void save()}>
        Save
      </Button>
      <Button size="sm" kind="ghost" disabled={busy} onPress={cancel}>
        Cancel
      </Button>
      {error && (
        <span className="text-xs text-bad truncate" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}
